import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { HistoryHero } from "@/components/site/history/HistoryHero";
import { HistoryTimeline } from "@/components/site/history/HistoryTimeline";
import { Reveal, WfButton } from "@/components/site/primitives";
import { HISTORY_INTRO } from "@/content/history";
import { SiteFooter } from "@/components/site/SiteFooter";

/**
 * HistoryPage — interactive, timeline-driven history experience.
 * NOTE: content is placeholder/filler for design-demo purposes (see content/history.js).
 */
export default function HistoryPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <HistoryHero />

        {HISTORY_INTRO.summary && (
          <section aria-label="History introduction" className="bg-[var(--wf-cream)] py-14 sm:py-16 lg:py-20">
            <div className="wf-container">
              <Reveal className="mx-auto max-w-3xl">
                <p className="text-lg leading-8 text-[var(--wf-soil)]">{HISTORY_INTRO.summary}</p>
              </Reveal>
            </div>
          </section>
        )}

        <HistoryTimeline />

        {/* Closing band */}
        <section aria-labelledby="history-outro-heading" className="bg-white py-16 sm:py-20 lg:py-24">
          <div className="wf-container">
            <div className="relative overflow-hidden rounded-sm bg-[var(--wf-farm-green)] px-6 py-14 text-center sm:px-10 sm:py-16">
              <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--wf-moss)]/40 blur-2xl" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-[var(--wf-olive)]/40 blur-2xl" />
              <div className="relative mx-auto max-w-2xl">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">The story continues</span>
                <h2 id="history-outro-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">
                  Be part of the next chapter.
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[rgba(255,253,248,0.85)]">
                  Four decades in, the fields are still growing. Support the farmers writing the next chapter of this story.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <WfButton as={Link} to="/support" variant="light" withArrow className="px-7" data-testid="history-outro-cta-button">
                    Get Involved
                  </WfButton>
                  <WfButton as="a" href="/" className="border border-white/60 bg-white/10 px-7 text-white backdrop-blur-sm hover:bg-white/20" data-testid="history-outro-home-button">
                    Back to home
                  </WfButton>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
